import {useCallback} from "react";
import {useAuthenticatedApi} from "@webng/react-app-common";
import {CommentList} from "./Models";
import {InfiniteData, QueryFunction, useInfiniteQuery} from "@tanstack/react-query";
import useChannelPreview from "./useChannelPreview";
import {ApiClient} from '@webng/tickaroo-api'


interface CommentListApiProps {
  ownerId: string
  channelId: string
  version?: number
  limit?: number
}

type UseCommentsQueryKey = ["useComments", string, string, number|undefined]

export default function useComments({ownerId, channelId, limit}: CommentListApiProps){
  const apiClient = useAuthenticatedApi();

  const queryFn: QueryFunction<CommentList, UseCommentsQueryKey, string|undefined> = useCallback(async ({queryKey, pageParam}) => {
    const [, ownerId, channelId, limit] = queryKey;
    return apiClient.get<CommentList>(ApiClient.API_COMMENTS_V1_PUBLIC_CHANNEL_COMMENTS, {
      ownerId: ownerId,
      channelId: channelId,
      limit: limit,
      next: pageParam
    })
  }, [apiClient]);


  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
    isError,
    isLoading,
    error
  } = useInfiniteQuery<CommentList, Object, InfiniteData<CommentList>, UseCommentsQueryKey, string|undefined>({
      queryKey: ["useComments", ownerId, channelId, limit],
      queryFn: queryFn,
      getNextPageParam: lastPage => lastPage.next,
      retry:true,
      refetchIntervalInBackground: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      initialPageParam: undefined
    })

  const {total} = useChannelPreview({ownerId, channelId})

  return {
    total,
    newCount: (total && data?.pages && data.pages.length > 0) ? total - data.pages[0].total : 0,

    data: data?.pages.flatMap((page) => page.items) || [],
    fetchNextPage: () => fetchNextPage(),

    hasNextPage,
    isFetchingNextPage,
    refetch: () => refetch(),
    isError,
    isLoading,
    isFetching,
    error
  }
}
